import { FaCopy, FaFileExcel, FaFileCsv, FaFilePdf, FaPrint } from 'react-icons/fa';
import { BsThreeDotsVertical } from 'react-icons/bs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

const AccountExportOptions = ({
  data,
  dropdownOpen,
  toggleDropdown,
  title,
  headers,
  dataMapper,
  subTitle,
  footer,
  printedBy,
  secondaryData,
  secondaryHeaders,
  secondaryDataMapper,
  secondaryTitle,
}) => {
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownOpen && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        toggleDropdown();
      }
    };
    if (dropdownOpen) document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [dropdownOpen, toggleDropdown]);

  // --- Shared PDF/Print Generator Styles ---
  const bwBody = { textColor: [0, 0, 0], lineWidth: 0.1, lineColor: [0, 0, 0] };
  const bwStyle = { fontSize: 8, textColor: [0, 0, 0], lineColor: [0, 0, 0], lineWidth: 0.1 };
  const bwAlt = { fillColor: [255, 255, 255] };

const generatePDFDocument = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // --- Hospital Header (ছবির মতো) ---
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Mukti Hospital', pageWidth / 2, 14, { align: 'center' });

    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(40, 40, 40);
    doc.text('Race Course, Sasongacha, Cumilla', pageWidth / 2, 19, { align: 'center' });
    doc.text('Pathology: 01673-625038  ·  Emergency: 01711-796905', pageWidth / 2, 23.5, { align: 'center' });

    // হেডার ডিভাইডার মোটা দাগ (Black horizontal rule)
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.8);
    doc.line(14, 27, pageWidth - 14, 27);

    // --- Report Title & Subtitle ---
    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(title, pageWidth / 2, 34, { align: 'center' });

    let finalY = 37;
    if (subTitle) {
      doc.setFontSize(8.5);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(60, 60, 60);
      doc.text(subTitle, pageWidth / 2, 39, { align: 'center' });
      finalY = 43;
    }

    // --- Main Table ---
    autoTable(doc, {
      head: [headers],
      body: data.map(dataMapper).map((row) => row.split('\t')),
      foot: footer || [],
      startY: finalY,
      theme: 'grid',
      headStyles: {
        fillColor: [240, 240, 240],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.2,
        lineColor: [0, 0, 0],
      },
      bodyStyles: bwBody,
      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.2,
        lineColor: [0, 0, 0],
      },
      styles: bwStyle,
      alternateRowStyles: bwAlt,
      columnStyles: {
        0: { halign: 'left', cellWidth: 10 },
        1: { halign: 'left', cellWidth: 24 },
        2: { halign: 'left', cellWidth: 28 },
        3: { halign: 'left' },
        4: { halign: 'left', cellWidth: 25 },
        5: { halign: 'left', cellWidth: 26 },
        6: { halign: 'left', cellWidth: 26 },
      },
      didDrawPage: () => {
        const printDate = new Date().toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        });

        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.2);
        doc.line(14, pageHeight - 12, pageWidth - 14, pageHeight - 12);

        doc.text(`Printed by: ${printedBy || 'Staff'}`, 14, pageHeight - 7);
        doc.text(`Printed: ${printDate}`, pageWidth / 2, pageHeight - 7, { align: 'center' });
        doc.text('Software Powered By: Microters · microters.com', pageWidth - 14, pageHeight - 7, { align: 'right' });
      },
    });

    return doc;
  };

  // --- Export Actions ---
  const copyToClipboard = () => {
    let text = `${title}\n${subTitle ? `${subTitle}\n` : ''}${headers.join('\t')}\n${data.map(dataMapper).join('\n')}`;
    if (footer && footer.length > 0) {
      const footerText = footer.map((row) => row.map((cell) => cell.content || cell).join('\t')).join('\n');
      text += `\n${footerText}`;
    }
    if (secondaryData && secondaryData.length > 0 && secondaryHeaders && secondaryDataMapper) {
      text += `\n\n${secondaryTitle || 'Details'}\n${secondaryHeaders.join('\t')}\n${secondaryData.map(secondaryDataMapper).join('\n')}`;
    }
    navigator.clipboard.writeText(text)
      .then(() => toast.success('Copied to clipboard!'))
      .catch(() => toast.error('Failed to copy.'));
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const excelRows = data.map((item) => {
      const row = dataMapper(item).split('\t');
      return headers.reduce((acc, header, idx) => ({ ...acc, [header]: row[idx] || '' }), {});
    });

    if (footer) {
      excelRows.push({
        [headers[0]]: 'TOTAL',
        [headers[headers.length - 2]]: data.reduce((s, r) => s + (Number(r.netAmount) || 0), 0).toFixed(2),
        [headers[headers.length - 1]]: data.reduce((s, r) => s + (Number(r.referAmount || r.netReferAmount) || 0), 0).toFixed(2),
      });
    }

    const ws = XLSX.utils.json_to_sheet(excelRows);
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${title.replace(/\s/g, '_')}.xlsx`);
  };

  const exportToCSV = () => {
    let lines = [headers.join(','), ...data.map(dataMapper)];
    if (secondaryData && secondaryData.length > 0 && secondaryHeaders && secondaryDataMapper) {
      lines.push('', secondaryTitle || 'Details', secondaryHeaders.join(','), ...secondaryData.map(secondaryDataMapper));
    }
    saveAs(new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' }), `${title.replace(/\s/g, '_')}.csv`);
  };

  const exportToPDF = () => {
    const doc = generatePDFDocument();
    doc.save(`${title.replace(/\s/g, '_')}.pdf`);
  };

  const handlePrint = () => {
    if (!data || data.length === 0) {
      toast.error('No data available to print');
      return;
    }
    const doc = generatePDFDocument();
    doc.autoPrint();
    const blobUrl = doc.output('bloburl');
    window.open(blobUrl, '_blank');
  };

  const handleExportClick = (format) => {
    switch (format) {
      case 'Copy': copyToClipboard(); break;
      case 'Excel': exportToExcel(); break;
      case 'CSV': exportToCSV(); break;
      case 'PDF': exportToPDF(); break;
      case 'Print': handlePrint(); break;
    }
    toggleDropdown();
  };

  return (
    <div className="relative leading-[1px]" ref={dropdownRef}>
      <button onClick={toggleDropdown} className="text-xl text-gray-600 hover:text-gray-800 z-10 transition-colors">
        <BsThreeDotsVertical />
      </button>

      {dropdownOpen && (
        <div className="absolute top-10 right-0 mt-2 w-48 bg-white shadow-xl rounded-lg z-[100] border border-gray-100 overflow-hidden">
          <div className="py-1">
            {[
              { label: 'Copy', icon: <FaCopy className="text-blue-500" /> },
              { label: 'Excel', icon: <FaFileExcel className="text-green-600" /> },
              { label: 'CSV', icon: <FaFileCsv className="text-teal-500" /> },
              { label: 'PDF', icon: <FaFilePdf className="text-red-500" /> },
              { label: 'Print', icon: <FaPrint className="text-gray-600" /> },
            ].map((item) => (
              <button
                key={item.label}
                onClick={() => handleExportClick(item.label)}
                className="flex items-center px-4 py-2.5 hover:bg-gray-50 w-full text-left text-sm text-gray-700 transition-colors border-b last:border-none border-gray-50"
              >
                <span className="mr-3">{item.icon}</span>
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountExportOptions;